// 🔧 engagement_activities.js
// ✅ Purpose: Manager page for creating and managing Engagement Engine activities
// ✅ Features:
// - Requires the Engagement Passport add-on
// - Allows managers/admins to create Engagement Activities
// - Supports public codes, QR codes, and bonus codes
// - Supports points, raffle entries, bonus points, and max completions
// - Stores normalized codes for Cloud Function lookup
// - Lists current activities for the event
// - Supports activating/deactivating activities
// - Supports deleting activities before heavy event use
// - Uses standard LGV success/error notifications for 20 seconds
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { hasPurchasedAddOn } from "../../utils/event_addons";
import AddonRequired from "../../components/AddonRequired";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import { logger } from "../../utils/logger";

const normalizeCode = (value = "") => {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
};

const STOP_TYPE_OPTIONS = [
  {
    value: "event",
    label: "🎪 Event Stop",
  },
  {
    value: "sponsor",
    label: "🤝 Sponsor Stop",
  },
  {
    value: "vendor",
    label: "🛍️ Vendor Stop",
  },
  {
    value: "bonus",
    label: "🎁 Bonus Stop",
  },
  {
    value: "hidden",
    label: "🗺️ Hidden Stop",
  },
  {
    value: "challenge",
    label: "🏆 Challenge Stop",
  },
  {
    value: "photo",
    label: "📸 Photo Stop",
  },
  {
    value: "volunteer",
    label: "🙋 Volunteer Stop",
  },
  {
    value: "education",
    label: "🎓 Learning Stop",
  },
];

const DEFAULT_FORM = {
  title: "",
  description: "",
  activityType: "event_location_checkin",
  stopType: "event",
  sourceModule: "events",
  sourceType: "event",
  sourceId: "",
  publicCode: "",
  qrCode: "",
  bonusCode: "",
  points: 1,
  raffleEntries: 1,
  bonusPoints: 5,
  bonusRaffleEntries: 5,
  maxCompletionsPerUser: 1,
  active: true,
  visibleToPublic: true,
};

const EngagementActivities = () => {
  const {
    eventId,
    eventslug,
    eventDoc,
    ready,
    eventLoading,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const passportEnabled = hasPurchasedAddOn(eventDoc, "passport");

  const [form, setForm] = useState(DEFAULT_FORM);
  const [activities, setActivities] = useState([]);
  const [status, setStatus] = useState(null);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [saving, setSaving] = useState(false);

  const activitiesRef = useMemo(() => {
    if (!eventId) return null;

    return collection(db, "events", eventId, "engagement_activities");
  }, [eventId]);

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  const loadActivities = async () => {
    if (!activitiesRef || !passportEnabled) {
      setActivities([]);
      setLoadingActivities(false);
      return;
    }

    try {
      setLoadingActivities(true);

      const snap = await getDocs(
        query(activitiesRef)
      );

      const rows = snap.docs.map((activityDoc) => ({
        id: activityDoc.id,
        ...activityDoc.data(),
      }));

      setActivities(rows);
    } catch (err) {
      logger.error("❌ Failed to load engagement activities:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to load engagement activities.",
      });
    } finally {
      setLoadingActivities(false);
    }
  };

  useEffect(() => {
    if (!ready || eventLoading) return;

    loadActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, eventLoading, activitiesRef, passportEnabled]);

  const handleChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (!form.title.trim()) {
      return "Activity title is required.";
    }

    if (!form.publicCode.trim() && !form.qrCode.trim() && !form.bonusCode.trim()) {
      return "At least one code is required: public code, QR code, or bonus code.";
    }

    if (Number(form.points) < 0) {
      return "Points cannot be negative.";
    }

    if (Number(form.raffleEntries) < 0) {
      return "Raffle entries cannot be negative.";
    }

    if (Number(form.maxCompletionsPerUser) < 0) {
      return "Max completions cannot be negative. Use 0 for unlimited.";
    }

    return "";
  };

  const handleCreateActivity = async (e) => {
    e.preventDefault();

    if (!activitiesRef) return;

    const validationMessage = validateForm();

    if (validationMessage) {
      showStatus(setStatus, {
        type: "error",
        message: validationMessage,
      });

      return;
    }

    try {
      setSaving(true);

      const publicCodeNormalized = normalizeCode(form.publicCode);
      const qrCodeNormalized = normalizeCode(form.qrCode);
      const bonusCodeNormalized = normalizeCode(form.bonusCode);

      await addDoc(activitiesRef, {
        title: form.title.trim(),
        description: form.description.trim(),

        activityType: form.activityType,
        stopType: form.stopType,
        sourceModule: form.sourceModule,
        sourceType: form.sourceType,
        sourceId: form.sourceId.trim(),

        publicCode: form.publicCode.trim(),
        publicCodeNormalized,

        qrCode: form.qrCode.trim(),
        qrCodeNormalized,

        bonusCode: form.bonusCode.trim(),
        bonusCodeNormalized,

        qrEnabled: Boolean(qrCodeNormalized),
        manualCodeEnabled: Boolean(publicCodeNormalized),
        bonusCodeEnabled: Boolean(bonusCodeNormalized),

        points: Number(form.points || 0),
        raffleEntries: Number(form.raffleEntries || 0),
        bonusPoints: Number(form.bonusPoints || 0),
        bonusRaffleEntries: Number(form.bonusRaffleEntries || 0),

        maxCompletionsPerUser: Number(form.maxCompletionsPerUser || 0),

        active: Boolean(form.active),
        visibleToPublic: Boolean(form.visibleToPublic),

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setForm(DEFAULT_FORM);

      showStatus(setStatus, {
        type: "success",
        message: "Engagement activity created successfully.",
      });

      await loadActivities();
    } catch (err) {
      logger.error("❌ Failed to create engagement activity:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to create engagement activity.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActivity = async (activity) => {
    try {
      await updateDoc(
        doc(db, "events", eventId, "engagement_activities", activity.id),
        {
          active: !activity.active,
          updatedAt: serverTimestamp(),
        }
      );

      showStatus(setStatus, {
        type: "success",
        message: activity.active
          ? "Activity deactivated."
          : "Activity activated.",
      });

      await loadActivities();
    } catch (err) {
      logger.error("❌ Failed to toggle engagement activity:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to update activity.",
      });
    }
  };

  const handleDeleteActivity = async (activity) => {
    const confirmDelete = window.confirm(
      `Delete "${activity.title}"? This should only be used before the activity has real event participation.`
    );

    if (!confirmDelete) return;

    try {
      await deleteDoc(
        doc(db, "events", eventId, "engagement_activities", activity.id)
      );

      showStatus(setStatus, {
        type: "success",
        message: "Activity deleted.",
      });

      await loadActivities();
    } catch (err) {
      logger.error("❌ Failed to delete engagement activity:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to delete activity.",
      });
    }
  };

  if (!ready || eventLoading) {
    return <FullPageSpinner message="Loading engagement activities..." />;
  }

  if (eventDoc && !passportEnabled) {
    return (
      <AddonRequired
        eventslug={eventslug}
        title="Engagement Passport Add-on Required"
        description="Purchase the Engagement Passport add-on before creating engagement activities."
      />
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12">
            <h1 className="display-5 mb-2">
              🧭 Engagement Activities
            </h1>

            <p className="lead text-muted mb-0">
              Activities are the measurable interactions that power the Engagement Passport and Event Intelligence.
            </p>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="row g-4">
          <div className="col-12 col-lg-5">
            <div className="card shadow-sm">
              <div className="card-header bg-white">
                <strong>Create Activity</strong>
              </div>

              <div className="card-body">
                <form onSubmit={handleCreateActivity}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Activity Title
                    </label>

                    <input
                      className="form-control"
                      value={form.title}
                      onChange={(e) => handleChange("title", e.target.value)}
                      placeholder="Sponsor Booth Visit"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Description
                    </label>

                    <textarea
                      className="form-control"
                      rows="3"
                      value={form.description}
                      onChange={(e) => handleChange("description", e.target.value)}
                      placeholder="Visit this location and enter the code to earn points."
                    />
                  </div>

                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-bold">
                        Activity Type
                      </label>
                      <div className="col-12 col-md-6">
                        <label className="form-label fw-bold">
                          Public Stop Type
                        </label>

                        <select
                          className="form-select"
                          value={form.stopType}
                          onChange={(e) => handleChange("stopType", e.target.value)}
                        >
                          {STOP_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>

                        <div className="form-text">
                          Controls how this stop appears to attendees.
                        </div>
                      </div>
                      <select
                        className="form-select"
                        value={form.activityType}
                        onChange={(e) => handleChange("activityType", e.target.value)}
                      >
                        <option value="event_location_checkin">Event Location Check-in</option>
                        <option value="sponsor_booth_visit">Sponsor Booth Visit</option>
                        <option value="sponsor_conversation_code">Sponsor Conversation Code</option>
                        <option value="vendor_booth_visit">Vendor Booth Visit</option>
                        <option value="vendor_purchase_code">Vendor Purchase Code</option>
                        <option value="volunteer_signup">Volunteer Signup</option>
                        <option value="volunteer_shift_completed">Volunteer Shift Completed</option>
                        <option value="auction_viewed">Auction Viewed</option>
                        <option value="auction_bid_placed">Auction Bid Placed</option>
                        <option value="vote_submitted">Vote Submitted</option>
                        <option value="hidden_stop_found">Hidden Stop Found</option>
                      </select>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-bold">
                        Source Module
                      </label>

                      <select
                        className="form-select"
                        value={form.sourceModule}
                        onChange={(e) => handleChange("sourceModule", e.target.value)}
                      >
                        <option value="events">Event</option>
                        <option value="sponsors">Sponsors</option>
                        <option value="vendors">Vendors</option>
                        <option value="volunteers">Volunteers</option>
                        <option value="auction">Auction</option>
                        <option value="voting">Voting</option>
                        <option value="schedule">Schedule</option>
                        <option value="store">Store</option>
                        <option value="tickets">Tickets</option>
                      </select>
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-bold">
                        Source Type
                      </label>

                      <input
                        className="form-control"
                        value={form.sourceType}
                        onChange={(e) => handleChange("sourceType", e.target.value)}
                        placeholder="sponsor, vendor, event, activity"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-bold">
                        Source ID
                      </label>

                      <input
                        className="form-control"
                        value={form.sourceId}
                        onChange={(e) => handleChange("sourceId", e.target.value)}
                        placeholder="Optional linked document ID"
                      />
                    </div>
                  </div>

                  <hr />

                  <div className="row g-3">
                    <div className="col-12 col-md-4">
                      <label className="form-label fw-bold">
                        Public Code
                      </label>

                      <input
                        className="form-control"
                        value={form.publicCode}
                        onChange={(e) => handleChange("publicCode", e.target.value)}
                        placeholder="SPONSOR25"
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label fw-bold">
                        QR Code
                      </label>

                      <input
                        className="form-control"
                        value={form.qrCode}
                        onChange={(e) => handleChange("qrCode", e.target.value)}
                        placeholder="QRSPONSOR25"
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label fw-bold">
                        Bonus Code
                      </label>

                      <input
                        className="form-control"
                        value={form.bonusCode}
                        onChange={(e) => handleChange("bonusCode", e.target.value)}
                        placeholder="TALKED25"
                      />
                    </div>
                  </div>

                  <hr />

                  <div className="row g-3">
                    <div className="col-6 col-md-3">
                      <label className="form-label fw-bold">
                        Points
                      </label>

                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={form.points}
                        onChange={(e) => handleChange("points", e.target.value)}
                      />
                    </div>

                    <div className="col-6 col-md-3">
                      <label className="form-label fw-bold">
                        Entries
                      </label>

                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={form.raffleEntries}
                        onChange={(e) => handleChange("raffleEntries", e.target.value)}
                      />
                    </div>

                    <div className="col-6 col-md-3">
                      <label className="form-label fw-bold">
                        Bonus Points
                      </label>

                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={form.bonusPoints}
                        onChange={(e) => handleChange("bonusPoints", e.target.value)}
                      />
                    </div>

                    <div className="col-6 col-md-3">
                      <label className="form-label fw-bold">
                        Bonus Entries
                      </label>

                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={form.bonusRaffleEntries}
                        onChange={(e) => handleChange("bonusRaffleEntries", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="form-label fw-bold">
                      Max Completions Per User
                    </label>

                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      value={form.maxCompletionsPerUser}
                      onChange={(e) =>
                        handleChange("maxCompletionsPerUser", e.target.value)
                      }
                    />

                    <div className="form-text">
                      Use 1 for once per attendee. Use 0 for unlimited.
                    </div>
                  </div>

                  <div className="form-check mt-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => handleChange("active", e.target.checked)}
                      id="activityActive"
                    />

                    <label className="form-check-label" htmlFor="activityActive">
                      Active
                    </label>
                  </div>

                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={form.visibleToPublic}
                      onChange={(e) =>
                        handleChange("visibleToPublic", e.target.checked)
                      }
                      id="activityVisible"
                    />

                    <label className="form-check-label" htmlFor="activityVisible">
                      Visible to public
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-success w-100 mt-4"
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Create Activity"}
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-7">
            <div className="card shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center">
                <strong>Current Activities</strong>

                <span className="badge bg-secondary">
                  {activities.length}
                </span>
              </div>

              <div className="card-body">
                {loadingActivities ? (
                  <div className="text-muted">
                    Loading activities...
                  </div>
                ) : activities.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    No engagement activities have been created yet.
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {activities.map((activity) => (
                      <div
                        key={activity.id}
                        className={`border rounded p-3 ${
                          activity.active ? "bg-white" : "bg-light"
                        }`}
                      >
                        <div className="d-flex justify-content-between gap-3">
                          <div>
                            <h5 className="fw-bold mb-1">
                              {activity.title}
                            </h5>

                            <div className="small text-muted">
                              {activity.stopType || "event"} · {activity.activityType} · {activity.sourceModule}
                            </div>
                          </div>

                          <div className="text-end">
                            <span
                              className={`badge ${
                                activity.active ? "bg-success" : "bg-secondary"
                              }`}
                            >
                              {activity.active ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>

                        {activity.description && (
                          <p className="text-muted mt-2 mb-2">
                            {activity.description}
                          </p>
                        )}

                        <div className="row g-2 mt-2 small">
                          <div className="col-12 col-md-4">
                            <strong>Public:</strong>{" "}
                            {activity.publicCode || "—"}
                          </div>

                          <div className="col-12 col-md-4">
                            <strong>QR:</strong>{" "}
                            {activity.qrCode || "—"}
                          </div>

                          <div className="col-12 col-md-4">
                            <strong>Bonus:</strong>{" "}
                            {activity.bonusCode || "—"}
                          </div>
                        </div>

                        <div className="row g-2 mt-2 small">
                          <div className="col-6 col-md-3">
                            Points: {activity.points || 0}
                          </div>

                          <div className="col-6 col-md-3">
                            Entries: {activity.raffleEntries || 0}
                          </div>

                          <div className="col-6 col-md-3">
                            Bonus: {activity.bonusPoints || 0}
                          </div>

                          <div className="col-6 col-md-3">
                            Max/User: {activity.maxCompletionsPerUser ?? 1}
                          </div>
                        </div>

                        <div className="d-flex gap-2 mt-3">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleToggleActivity(activity)}
                          >
                            {activity.active ? "Deactivate" : "Activate"}
                          </button>

                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDeleteActivity(activity)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="alert alert-light border mt-3 mb-0">
              <strong>Tip:</strong> Activities are the Engagement Engine foundation.
              Sponsor visits, vendor purchases, scavenger hunt stops, schedule check-ins,
              volunteer rewards, and hidden codes all use this same model.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EngagementActivities;
